import { apiDelete, apiGet, apiPost, apiPut } from "../_apiShim";
import { EventGoalDoc, KpiHistorySeries, KpiValue } from "@/types/EventGoal";
import { wireTargetDirection } from "@/lib/goals/targetDirection";

const BASE_PATH = "/api/v1/event-goals/";

/** One stored value of a cohort facet and how many active guests carry it. */
export type CohortValue = { value: string; count: number };

/**
 * `GET …/cohort-values` — the distinct stored values of ONE cohort facet over
 * the active roster (the population the analysis and a cohort-share KPI
 * count), most frequent first. `truncated` = more values than `limit`;
 * `options` = the field definition's SELECT options (null for a built-in or
 * a field without one); `dataType` = the additional field's type (null for a
 * built-in).
 */
export type CohortValuesResponse = {
  dimension: string;
  total: number;
  values: CohortValue[];
  truncated: boolean;
  dataType?: string | null;
  options?: string[] | null;
};

/** Get the event's goal doc (backend returns an empty default when none exists). */
export async function getEventGoals(eventId: string): Promise<EventGoalDoc> {
  const response = await apiGet<EventGoalDoc>(
    `${BASE_PATH}${eventId}`,
    { "Cache-Control": "no-store" },
    true,
  );

  if (!response) throw new Error("unknown");

  return response;
}

/**
 * Upsert goalText + kpis + alertConfig. Server merges KPI rows by `id`,
 * preserves alert state + analysis, mints missing ids. Returns the stored doc.
 */
export async function updateEventGoals(
  eventId: string,
  doc: Pick<EventGoalDoc, "goalText" | "kpis" | "cohortTargets" | "alertConfig">,
): Promise<EventGoalDoc> {
  const response = await apiPut<EventGoalDoc>(
    `${BASE_PATH}${eventId}`,
    {
      goalText: doc.goalText,
      kpis: doc.kpis.map((kpi) => ({
        id: kpi.id,
        metric: kpi.metric,
        target: kpi.target,
        thresholdValue: kpi.thresholdValue,
        thresholdDirection: kpi.thresholdDirection,
        deadline: kpi.deadline,
        enabled: kpi.enabled,
        // The two cohort fields exist ONLY on COHORT_SHARE — the backend nulls
        // them elsewhere, but don't send what means nothing.
        // `outcome` always travels as a boolean on a cohort KPI: the backend
        // keeps a stored flag only when a client that does not know it sends
        // nothing (an older tab during a deploy).
        // The group (W3) travels the same way: "" = none, so the backend can
        // tell "no group" from a tab that never knew the field.
        ...(kpi.metric === "COHORT_SHARE"
          ? {
              dimension: kpi.dimension,
              bucketValue: kpi.bucketValue,
              outcome: kpi.outcome === true,
              baseDimension: kpi.baseDimension && kpi.baseValue ? kpi.baseDimension : "",
              baseValue: kpi.baseDimension && kpi.baseValue ? kpi.baseValue : "",
            }
          : {}),
      })),
      cohortTargets: (doc.cohortTargets ?? []).map((target) => ({
        dimension: target.dimension,
        label: target.label,
        targetPercent: target.targetPercent,
        // EVERY row names its direction (audit r1 F-L2, see wireTargetDirection):
        // absent would make the backend keep the stored one.
        direction: wireTargetDirection(target.direction),
      })),
      alertConfig: doc.alertConfig,
    },
    { "Cache-Control": "no-store" },
    true,
  );

  if (!response) throw new Error("unknown");

  return response;
}

export async function deleteEventGoals(eventId: string): Promise<void> {
  await apiDelete(`${BASE_PATH}${eventId}`, { "Cache-Control": "no-store" }, undefined, true);
}

/** Live computed values for every defined KPI. */
export async function getKpiValues(eventId: string): Promise<KpiValue[]> {
  const response = await apiGet<KpiValue[]>(
    `${BASE_PATH}${eventId}/kpi-values`,
    { "Cache-Control": "no-store" },
    true,
  );

  if (!response) return [];

  return response;
}

/**
 * Distinct stored values of one cohort facet (see {@link CohortValuesResponse}).
 * 403 `kpiCohortForbidden` for a role that may not read the facet (no
 * guests:read, row-restricted, or the field is outside its allowlist), 400
 * `kpiDimensionInvalid` for an unknown dimension — both throw; the hook maps
 * them to "no suggestions".
 */
export async function getCohortValues(
  eventId: string,
  dimension: string,
  limit: number = 200,
): Promise<CohortValuesResponse> {
  const response = await apiGet<CohortValuesResponse>(
    `${BASE_PATH}${eventId}/cohort-values?dimension=${encodeURIComponent(dimension)}&limit=${encodeURIComponent(String(limit))}`,
    { "Cache-Control": "no-store" },
    true,
  );

  if (!response || !Array.isArray(response.values)) throw new Error("unknown");

  return response;
}

/**
 * Daily KPI history (one point per event-local calendar day, oldest first) —
 * the series behind the sparkline, the 7-day trend and the forecast. One
 * request for all KPIs. History starts when a KPI was created; an empty series
 * is normal, not an error.
 */
export async function getKpiHistory(eventId: string, days: number): Promise<KpiHistorySeries[]> {
  const response = await apiGet<KpiHistorySeries[]>(
    `${BASE_PATH}${eventId}/kpi-history?days=${encodeURIComponent(String(days))}`,
    { "Cache-Control": "no-store" },
    true,
  );

  if (!Array.isArray(response)) return [];

  return response;
}

/**
 * Persist one dismissed gap recommendation. `title` must be the EXACT
 * persisted gap title (the server 400s "unknownGapTitle" otherwise).
 */
export async function dismissGoalGap(eventId: string, title: string): Promise<void> {
  const response = await apiPost<{ ok: boolean }>(
    `${BASE_PATH}${eventId}/gaps/dismiss`,
    { title },
    { "Cache-Control": "no-store" },
    true,
  );

  if (!response) throw new Error("unknown");
}

/** Clears every dismissed gap ("Wieder anzeigen"). */
export async function restoreGoalGaps(eventId: string): Promise<void> {
  const response = await apiPost<{ ok: boolean }>(
    `${BASE_PATH}${eventId}/gaps/restore`,
    {},
    { "Cache-Control": "no-store" },
    true,
  );

  if (!response) throw new Error("unknown");
}

/** Kick off the AI cohort analysis. 202 + jobId; poll the background job. */
export async function startGoalAnalysis(eventId: string): Promise<{ jobId: string }> {
  const response = await apiPost<{ jobId: string }>(
    `${BASE_PATH}${eventId}/analyze`,
    {},
    { "Cache-Control": "no-store" },
    true,
  );

  if (!response?.jobId) throw new Error("unknown");

  return response;
}
